// MARK:- Volume bundle builder — stages eligible items locally (sessionStorage), shows live
// tier progress for matched cart lines + staged items, and commits the whole build in one
// atomic /cart/add. Matched cart lines are also editable in the panel (per-line /cart/change)
// so it behaves as a mini-cart before and after commit. Tiers + eligibility arrive via
// data-discount (Discount Kit Live JSON resolved by custom-volume-discount-data.liquid).
// All clicks are delegated from this root so cards appended by custom-pagination.js
// load-more keep working with no rebinding. No observers, no polling — plain listeners
// bound once in connectedCallback and removed in disconnectedCallback.
if (!customElements.get('custom-volume-bundler')) {
  class CustomVolumeBundler extends HTMLElement {
    connectedCallback() {
      this.bindFilters();
      if (!this.dataset.discount) return;
      if (!window.NHVolumeSavings) {
        console.warn('custom-volume-bundler: NHVolumeSavings not loaded'); // eslint-disable-line no-console
        return;
      }

      try {
        this.discount = JSON.parse(this.dataset.discount);
      } catch (err) {
        console.error('custom-volume-bundler: invalid discount data', err); // eslint-disable-line no-console
        return;
      }
      this.tiers = (this.discount && this.discount.tiers) || [];
      if (this.tiers.length === 0) return;

      this.maxTierQty = this.tiers[this.tiers.length - 1].qty;
      this.storageKey = this.dataset.storageKey;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.nounSingular = this.dataset.nounSingular || 'item';
      this.nounPlural = this.dataset.nounPlural || 'items';
      this.maxNoteText = this.dataset.maxNote || 'Only ==quantity== available';

      // MARK:- Matched-cart seed from Liquid; ids also feed the classification set used on cart.js refetches
      this.cartState = CustomVolumeBundler.readCartState(this);
      this.staged = new Map();
      this.committing = false;
      this.lineTimers = new Map();
      this.pendingLineQty = new Map();
      this.lineInflight = new Set();
      this.shipState = this.resolveShipState();
      // MARK:- State named by a Plant Sentry block (judges the cart's delivery address); session-only override of shipState
      this.sentryState = null;
      this.recovery = null;

      this.cacheRefs();
      this.bindEvents();
      this.hydrate();
      this.render();
    }

    disconnectedCallback() {
      if (this.gridEmptyHandler) this.removeEventListener('on:pagination:results-added', this.gridEmptyHandler);
      if (this.filterToggleHandler) this.removeEventListener('click', this.filterToggleHandler);
      if (this.facetsCloseHandler) this.facets.removeEventListener('on:facet-filters:before-close', this.facetsCloseHandler);
      if (this.facetsBreakpointHandler) window.removeEventListener('on:breakpoint-change', this.facetsBreakpointHandler);
      if (this.clickHandler) this.removeEventListener('click', this.clickHandler);
      if (this.resultsHandler) this.removeEventListener('on:pagination:results-added', this.resultsHandler);
      if (this.resultsHandler) document.removeEventListener('on:facet-filters:updated', this.resultsHandler);
      if (this.keyHandler) document.removeEventListener('keydown', this.keyHandler);
      if (this.cartEventHandler) {
        CustomVolumeBundler.CART_EVENTS.forEach((name) => {
          document.removeEventListener(name, this.cartEventHandler);
        });
      }
      if (this.standardCartHandler) document.removeEventListener('shopify:cart:lines-update', this.standardCartHandler);
      if (this.pageShowHandler) window.removeEventListener('pageshow', this.pageShowHandler);
      if (this.zoneHandler) {
        CustomVolumeBundler.ZONE_EVENTS.forEach((name) => document.removeEventListener(name, this.zoneHandler));
      }
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.lineTimers.forEach((timer) => clearTimeout(timer));
      this.lineTimers.clear();
    }

    // MARK:- Cart state from the element's Liquid-rendered data attributes — used at boot and
    // again on Section Rendering API refetches (sectionSync), so classification always comes
    // from Liquid's metafield match rather than the id-membership heuristic in applyCartData
    static readCartState(el) {
      const state = {
        count: parseInt(el.dataset.cartCount, 10) || 0,
        subtotal: parseInt(el.dataset.cartSubtotal, 10) || 0,
        saved: parseInt(el.dataset.cartSaved, 10) || 0,
        ids: new Set((el.dataset.cartIds || '').split(',').filter(Boolean).map(Number)),
        lines: new Map()
      };
      try {
        const lines = JSON.parse(el.dataset.cartLines || '[]');
        lines.forEach((line) => {
          if (line && line.key) state.lines.set(line.key, line);
        });
      } catch (err) {
        console.error('custom-volume-bundler: invalid cart lines data', err); // eslint-disable-line no-console
      }
      return state;
    }

    cacheRefs() {
      this.refs = {
        panel: this.querySelector('[data-volume-panel]'),
        peek: this.querySelector('[data-volume-peek]'),
        peekText: this.querySelector('[data-volume-peek-text]'),
        peekFill: this.querySelector('[data-volume-peek-fill]'),
        scrim: this.querySelector('[data-volume-scrim]'),
        headline: this.querySelector('[data-volume-headline]'),
        ladderFill: this.querySelector('[data-volume-ladder-fill]'),
        tiers: [...this.querySelectorAll('[data-volume-tier]')],
        count: this.querySelector('[data-volume-count]'),
        empty: this.querySelector('[data-volume-empty]'),
        list: this.querySelector('[data-volume-list]'),
        rowTemplate: this.querySelector('[data-volume-row-template]'),
        stagedGroup: this.querySelector('[data-volume-staged-group]'),
        cartGroup: this.querySelector('[data-volume-cart-group]'),
        cartList: this.querySelector('[data-volume-cart-list]'),
        cartRowTemplate: this.querySelector('[data-volume-cart-row-template]'),
        subtotal: this.querySelector('[data-volume-subtotal]'),
        saveRow: this.querySelector('[data-volume-save-row]'),
        saveAmount: this.querySelector('[data-volume-save-amount]'),
        savePct: this.querySelector('[data-volume-save-pct]'),
        recovery: this.querySelector('[data-volume-recovery]'),
        recoveryText: this.querySelector('[data-volume-recovery-text]'),
        recoveryBtn: this.querySelector('[data-volume-recovery-btn]'),
        hint: this.querySelector('[data-volume-hint]'),
        hintText: this.querySelector('[data-volume-hint-text]'),
        alert: this.querySelector('[data-volume-alert]'),
        commit: this.querySelector('[data-volume-commit]')
      };
    }

    bindEvents() {
      this.clickHandler = this.handleClick.bind(this);
      this.addEventListener('click', this.clickHandler);

      // MARK:- Full render on load-more (not just syncCards) — appended cards carry fresh data-max,
      // and reclampStaged must clamp any sessionStorage qty staged against a card that just arrived
      this.resultsHandler = () => this.render();
      this.addEventListener('on:pagination:results-added', this.resultsHandler);
      // MARK:- Facet/sort swaps replace #filter-results wholesale (facet-filters.js dispatches on document) — same full render; staged state lives in this.staged, not the cards
      document.addEventListener('on:facet-filters:updated', this.resultsHandler);

      this.keyHandler = (evt) => {
        if (evt.key === 'Escape' && this.classList.contains('is-expanded')) this.collapse();
      };
      document.addEventListener('keydown', this.keyHandler);

      // MARK:- Cart changes elsewhere (drawer qty edits, other add-to-carts) -> debounced resync.
      // Events dispatched from inside this element (our own commit / line changes) are ignored —
      // those paths apply their own response, and re-listening would echo into a resync loop.
      this.cartEventHandler = (evt) => {
        if (evt.target instanceof Node && this.contains(evt.target)) return;
        this.scheduleCartSync();
      };
      CustomVolumeBundler.CART_EVENTS.forEach((name) => {
        document.addEventListener(name, this.cartEventHandler);
      });

      // MARK:- Back/forward cache restores the old DOM without re-running Liquid (e.g. add from a
      // PDP, then browser Back) — resync from the Section Rendering API on a persisted pageshow
      this.pageShowHandler = (evt) => {
        if (evt.persisted) this.sectionSync();
      };
      window.addEventListener('pageshow', this.pageShowHandler);

      // MARK:- Standard events bridge — app/agent changes via Shopify.actions.updateCart arrive as
      // shopify:cart:lines-update, which fires at operation start; sync after its promise settles.
      // Our own commit dispatch is skipped (this element re-renders itself on commit).
      this.standardCartHandler = (evt) => {
        if (this.contains(evt.target)) return;
        if (evt.promise) {
          evt.promise.then(() => this.scheduleCartSync()).catch(() => {});
        } else {
          this.scheduleCartSync();
        }
      };
      document.addEventListener('shopify:cart:lines-update', this.standardCartHandler);

      // MARK:- Zone finder lookups (grow-zone-finder.js) can change the shopper's state; re-judge every card and staged row
      this.zoneHandler = (evt) => {
        this.sentryState = null;
        this.shipState = this.resolveShipState(evt.detail || {});
        this.render();
      };
      CustomVolumeBundler.ZONE_EVENTS.forEach((name) => document.addEventListener(name, this.zoneHandler));
    }

    // MARK:- Shopper state, resolved like custom-cart-shipping-restrictions.js: a zone event's stateCode,
    // else localStorage growing_state, else (at load only) the Liquid fallback in data-ship-state
    // (cart attribute customer_state, then a US default address). A zone event without a state
    // (legacy lookup) skips the Liquid fallback, which may predate the new ZIP. Null = unknown.
    // Shared with the cart suggestions' <ship-filter> (NHVolumeSavings.pickShipState)
    resolveShipState(detail) {
      const savings = window.NHVolumeSavings;
      return savings.pickShipState(detail, savings.savedShipState(), this.dataset.shipState);
    }

    stagedRestricted(meta) {
      return window.NHVolumeSavings.isRestricted(meta.restricted, this.shipState);
    }

    // MARK:- Filter drawer — bound before the discount guard so a browse-only grid still filters.
    // products-toolbar.js only toggles a .main-products-grid__filters sidebar, which this layout
    // doesn't have, so the toolbar's Filter button opens <facet-filters> as a drawer here. At >=1024px
    // facet-filters docks itself open (sidebar mode) on init and on every breakpoint event (xl/xxl/
    // portrait crossings included); undock it after its handler runs so it stays off-canvas at every
    // width (CSS side in custom-volume-bundler.css). facetsUserOpen separates a self-dock from a
    // drawer the shopper opened, which must survive resize, zoom and rotation.
    bindFilters() {
      this.gridEmptyHandler = () => setTimeout(() => this.syncGridEmpty());
      this.addEventListener('on:pagination:results-added', this.gridEmptyHandler);

      this.facets = this.querySelector('facet-filters');
      if (!this.facets) return;
      this.facetsUserOpen = false;
      this.filterToggleHandler = (evt) => {
        const btn = evt.target.closest('.js-toggle-filters');
        if (!btn || !this.facets.open) return;
        this.facetsUserOpen = true;
        btn.setAttribute('aria-expanded', 'true');
        this.facets.open(btn);
      };
      this.addEventListener('click', this.filterToggleHandler);
      // MARK:- Every SideDrawer close path (X, overlay, Escape, "Show results", facet-filters' own <1024 close) dispatches before-close
      this.facetsCloseHandler = () => {
        this.facetsUserOpen = false;
        this.querySelectorAll('.js-toggle-filters').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      };
      this.facets.addEventListener('on:facet-filters:before-close', this.facetsCloseHandler);
      this.facetsBreakpointHandler = () => setTimeout(() => this.undockFacets());
      window.addEventListener('on:breakpoint-change', this.facetsBreakpointHandler);
      customElements.whenDefined('facet-filters').then(() => this.undockFacets());
    }

    // MARK:- facet-filters' dock strips role/aria-modal; restore them, and close only a self-docked
    // drawer. Attributes are reset directly: close() runs removeTrapFocus(opener), which would pull
    // focus and scroll to the Filter button, and a self-dock never added the overlay or focus trap.
    undockFacets() {
      if (!window.theme || !theme.mediaMatches || !theme.mediaMatches.lg) return;
      this.facets.setAttribute('role', 'dialog');
      this.facets.setAttribute('aria-modal', 'true');
      if (this.facetsUserOpen || !this.facets.hasAttribute('open')) return;
      this.facets.removeAttribute('open');
      this.facets.setAttribute('aria-hidden', 'true');
    }

    // MARK:- A first page with no eligible card hides its empty message while later pages exist
    // (the Liquid leaves a hidden .js-pagination-result anchor for load-more); show it once the last
    // page has loaded (custom-pagination removed .js-pagination) and still no card rendered
    syncGridEmpty() {
      const empty = this.querySelector('[data-volume-grid-empty]');
      if (!empty || !empty.hidden || this.querySelector('[data-volume-card]')) return;
      if (!this.querySelector('.js-pagination')) empty.hidden = false;
    }

    handleClick(evt) {
      const { target } = evt;

      // MARK:- aria-disabled (not disabled) keeps focus on capped + buttons — so the click guard lives here
      const btn = target.closest('button');
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;

      const card = target.closest('[data-volume-card]');
      if (card) {
        if (target.closest('[data-volume-add]') || target.closest('[data-volume-inc]')) {
          this.changeStaged(card, 1);
          return;
        }
        if (target.closest('[data-volume-dec]')) {
          this.changeStaged(card, -1);
          return;
        }
      }

      const row = target.closest('[data-volume-item]');
      if (row) {
        const variantId = Number(row.dataset.variantId);
        if (target.closest('[data-volume-item-inc]')) {
          this.changeStagedById(variantId, 1);
          return;
        }
        if (target.closest('[data-volume-item-dec]')) {
          this.changeStagedById(variantId, -1);
          return;
        }
        // MARK:- Remove (shown on a row that can't ship to the shopper's state) drops the whole staged qty
        if (target.closest('[data-volume-item-remove]')) {
          this.changeStagedById(variantId, -Infinity);
          return;
        }
      }

      const cartRow = target.closest('[data-volume-cart-item]');
      if (cartRow) {
        if (target.closest('[data-volume-cart-inc]')) {
          this.changeCartLine(cartRow.dataset.lineKey, 1);
          return;
        }
        if (target.closest('[data-volume-cart-dec]')) {
          this.changeCartLine(cartRow.dataset.lineKey, -1);
          return;
        }
      }

      if (target.closest('[data-volume-peek]')) {
        this.toggleExpanded();
        return;
      }
      if (target.closest('[data-volume-scrim]')) {
        this.collapse();
        return;
      }
      if (target.closest('[data-volume-recovery-btn]')) {
        this.runRecovery();
        return;
      }
      if (target.closest('[data-volume-commit]')) {
        this.commit();
      }
    }

    // MARK:- Total in-cart qty per variant id — one variant can span a bundler-tagged and an untagged PDP line, so every cap sums across lines
    buildInCartMap() {
      const inCart = new Map();
      this.cartState.lines.forEach((line) => {
        inCart.set(Number(line.id), (inCart.get(Number(line.id)) || 0) + line.qty);
      });
      this.inCartMap = inCart;
    }

    // MARK:- How many more can be staged — inventory cap minus what the cart already holds (null = untracked/oversell allowed, no cap)
    effectiveMax(variantId, absMax) {
      if (absMax == null) return null;
      return Math.max(0, absMax - (this.inCartMap.get(variantId) || 0));
    }

    maxNote(qty) {
      return this.maxNoteText.replace('==quantity==', qty);
    }

    static cardMax(card) {
      return card.dataset.max !== undefined ? parseInt(card.dataset.max, 10) : null;
    }

    // MARK:- sessionStorage snapshots of meta.max go stale (admin edits, other-tab adds) — refresh
    // from the rendered cards and re-clamp staged qtys before every paint, so commits can't 422 on
    // inventory the page already knows about
    reclampStaged() {
      let changed = false;
      const cardMaxes = new Map();
      const cardStates = new Map();
      this.querySelectorAll('[data-volume-card]').forEach((card) => {
        cardMaxes.set(Number(card.dataset.variantId), CustomVolumeBundler.cardMax(card));
        cardStates.set(Number(card.dataset.variantId), window.NHVolumeSavings.parseStates(card.dataset.restrictedStates));
      });
      this.staged.forEach((meta, variantId) => {
        // MARK:- Restricted list rides on the staged meta so a row stays flagged after its card scrolls or filters away
        if (cardStates.has(variantId) && String(cardStates.get(variantId)) !== String(meta.restricted || [])) {
          meta.restricted = cardStates.get(variantId);
          changed = true;
        }
        let { max } = meta;
        if (cardMaxes.has(variantId)) max = cardMaxes.get(variantId);
        const eff = this.effectiveMax(variantId, max);
        let qty = meta.qty;
        if (eff !== null && qty > eff) qty = eff;
        if (max === meta.max && qty === meta.qty) return;
        changed = true;
        if (qty <= 0) {
          this.staged.delete(variantId);
        } else {
          this.staged.set(variantId, { ...meta, qty, max });
        }
      });
      if (changed) this.persist();
    }

    // MARK:- Staging — card dataset carries the metadata a panel row needs; qty is clamped to
    // the inventory left after subtracting what the cart holds (effectiveMax)
    changeStaged(card, delta) {
      const variantId = Number(card.dataset.variantId);
      if (!variantId) return;
      const restricted = window.NHVolumeSavings.parseStates(card.dataset.restrictedStates);
      if (delta > 0 && window.NHVolumeSavings.isRestricted(restricted, this.shipState)) return;
      const existing = this.staged.get(variantId);
      const meta = existing || {
        qty: 0,
        price: parseInt(card.dataset.price, 10) || 0,
        title: card.dataset.title || '',
        url: card.dataset.url || '',
        image: card.dataset.image || '',
        max: null
      };
      // MARK:- The clicked card always carries the freshest Liquid max — refresh even on an existing meta
      meta.max = CustomVolumeBundler.cardMax(card);
      meta.restricted = restricted;
      this.applyQtyChange(variantId, meta, delta);
      if (delta > 0) this.pulsePeek();
    }

    changeStagedById(variantId, delta) {
      const meta = this.staged.get(variantId);
      if (!meta) return;
      this.applyQtyChange(variantId, meta, delta);
    }

    applyQtyChange(variantId, meta, delta) {
      this.hideRecovery();
      let qty = meta.qty + delta;
      const eff = this.effectiveMax(variantId, meta.max);
      if (eff !== null && qty > eff) qty = eff;
      if (qty <= 0) {
        this.staged.delete(variantId);
      } else {
        this.staged.set(variantId, { ...meta, qty });
      }
      this.persist();
      this.render();
    }

    clearStaged() {
      this.staged.clear();
      this.persist();
    }

    // MARK:- sessionStorage via theme.storageUtil (auto-prefixes 'cc-'); per-collection key so parallel bundlers never collide
    persist() {
      if (!window.theme || !theme.storageUtil) return;
      const obj = {};
      this.staged.forEach((meta, id) => {
        obj[id] = meta;
      });
      theme.storageUtil.set(this.storageKey, obj, true);
    }

    hydrate() {
      if (!window.theme || !theme.storageUtil) return;
      const stored = theme.storageUtil.get(this.storageKey, true, true);
      if (!stored || typeof stored !== 'object') return;
      Object.entries(stored).forEach(([id, meta]) => {
        if (meta && meta.qty > 0) this.staged.set(Number(id), meta);
      });
    }

    stagedCount() {
      let count = 0;
      this.staged.forEach((meta) => {
        count += meta.qty;
      });
      return count;
    }

    stagedSubtotal() {
      let subtotal = 0;
      this.staged.forEach((meta) => {
        subtotal += meta.price * meta.qty;
      });
      return subtotal;
    }

    currentTier(combined) {
      let current = null;
      this.tiers.forEach((tier) => {
        if (combined >= tier.qty) current = tier;
      });
      return current;
    }

    nextTier(combined) {
      return this.tiers.find((tier) => combined < tier.qty) || null;
    }

    static escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, amount);
    }

    // MARK:- { price, qty } lines for NHVolumeSavings (assets/custom-volume-savings.js, the shared rounding rule)
    savingsLines() {
      const cart = [...this.cartState.lines.values()].map((line) => ({ price: line.price, qty: line.qty }));
      const staged = [...this.staged.values()].map((meta) => ({ price: meta.price, qty: meta.qty }));
      return { cart, staged };
    }

    // MARK:- Fill price for the plants still to add — cheapest rendered card, else null (helper falls back to the cheapest counted line)
    fillPrice() {
      let min = null;
      this.querySelectorAll('[data-volume-card]').forEach((card) => {
        const price = parseInt(card.dataset.price, 10);
        if (price > 0 && (min === null || price < min)) min = price;
      });
      return min;
    }

    render() {
      this.buildInCartMap();
      this.reclampStaged();
      const stagedCount = this.stagedCount();
      const combined = this.cartState.count + stagedCount;
      const current = this.currentTier(combined);
      const next = this.nextTier(combined);
      const progress = Math.min((combined / this.maxTierQty) * 100, 100);
      const remaining = next ? next.qty - combined : 0;
      const remainingNoun = remaining === 1 ? this.nounSingular : this.nounPlural;
      const savings = window.NHVolumeSavings;
      const lines = this.savingsLines();
      const subtotal = this.cartState.subtotal + this.stagedSubtotal();
      const saveRow = savings.savingsRow(this.tiers, this.cartState.saved, lines.cart, lines.staged);
      const fmt = (cents) => this.formatMoney(cents);

      // MARK:- Free-ship suffix needs an estimated post-discount total at the named tier: bundle lines
      // plus staged (plus the plants still to add at the fill price), less the tier discount; no fill price = unknown
      const fill = this.fillPrice();
      let total = null;
      if (!next) total = subtotal - saveRow.cents;
      else if (fill != null) total = subtotal + remaining * fill - savings.estimateNextTier(this.tiers, combined, lines.cart.concat(lines.staged), fill);

      // MARK:- Nouns are merchant settings — HTML-escape before innerHTML interpolation (numbers are parsed ints)
      const nounHtml = CustomVolumeBundler.escapeHtml(remainingNoun);
      if (this.refs.headline) {
        this.refs.headline.textContent = savings.nextCopy(this.tiers, combined, { one: this.nounSingular, other: this.nounPlural }, total);
      }
      if (this.refs.peekText) {
        // MARK:- From the first tier on, the strip leads with the money saved (custom-volume-savings.js peekCopy)
        const peek = savings.peekCopy(this.tiers, combined, saveRow.cents, total, fmt);
        if (peek) {
          this.refs.peekText.textContent = peek;
        } else {
          this.refs.peekText.innerHTML = next
            ? `Add <b>${remaining}</b> more ${nounHtml} &rarr; <b>${next.pct}% off</b>`
            : `<b>${current.pct}% off</b> unlocked`;
        }
      }
      if (this.refs.ladderFill) this.refs.ladderFill.style.setProperty('--progress', `${progress}%`);
      if (this.refs.peekFill) this.refs.peekFill.style.setProperty('--progress', `${progress}%`);

      this.refs.tiers.forEach((tierEl) => {
        const qty = Number(tierEl.dataset.tierQty);
        const reached = combined >= qty;
        const isNext = next !== null && next.qty === qty;
        tierEl.classList.toggle('is-reached', reached);
        tierEl.classList.toggle('is-next', isNext);
        const flag = tierEl.querySelector('[data-volume-tier-flag]');
        if (flag) {
          flag.hidden = !isNext;
          flag.textContent = isNext ? `${remaining} to go` : '';
        }
      });

      if (this.refs.count) this.refs.count.textContent = combined;
      if (this.refs.empty) this.refs.empty.hidden = combined > 0;

      this.renderCartList();
      this.renderList();
      this.renderTotals(current, next, combined, saveRow);
      this.syncCards();

      // MARK:- A staged plant that can't ship to the shopper's state blocks the commit until it is removed
      let blocked = 0;
      this.staged.forEach((meta) => {
        if (this.stagedRestricted(meta)) blocked += 1;
      });
      if (blocked > 0) {
        this.showAlert(savings.restrictedAlert(blocked, this.shipState));
        this.restrictionAlert = true;
      } else if (this.restrictionAlert) {
        this.showAlert('');
        this.restrictionAlert = false;
      }

      if (this.refs.commit) {
        this.refs.commit.disabled = stagedCount === 0 || this.committing || blocked > 0;
      }
    }

    renderList() {
      if (!this.refs.list || !this.refs.rowTemplate) return;
      const fragment = document.createDocumentFragment();
      this.staged.forEach((meta, variantId) => {
        const row = this.refs.rowTemplate.content.firstElementChild.cloneNode(true);
        row.dataset.variantId = variantId;
        CustomVolumeBundler.setThumb(row, meta.image, meta.title);
        const name = row.querySelector('[data-volume-item-name]');
        name.textContent = meta.title;
        name.href = meta.url;
        row.querySelector('[data-volume-item-price]').textContent = `${this.formatMoney(meta.price)} × ${meta.qty}`;
        row.querySelector('[data-volume-item-qty]').textContent = meta.qty;
        const eff = this.effectiveMax(variantId, meta.max);
        if (eff !== null && meta.qty >= eff) {
          row.querySelector('[data-volume-item-inc]').setAttribute('aria-disabled', 'true');
        }
        if (this.stagedRestricted(meta)) {
          row.classList.add('is-restricted');
          const flag = row.querySelector('[data-volume-item-restricted]');
          flag.textContent = window.NHVolumeSavings.restrictedCopy(this.shipState);
          flag.hidden = false;
          row.querySelector('[data-volume-item-stepper]').hidden = true;
          const remove = row.querySelector('[data-volume-item-remove]');
          remove.hidden = false;
          remove.setAttribute('aria-label', `Remove ${meta.title} from your bundle`);
        }
        fragment.appendChild(row);
      });
      this.refs.list.replaceChildren(fragment);
      if (this.refs.stagedGroup) this.refs.stagedGroup.hidden = this.staged.size === 0;
    }

    // MARK:- Editable in-cart rows — same row markup as custom-volume-builder-cart-row.liquid (SSR), keyed by line key for /cart/change
    renderCartList() {
      if (!this.refs.cartList || !this.refs.cartRowTemplate) return;
      const fragment = document.createDocumentFragment();
      this.cartState.lines.forEach((line, key) => {
        const row = this.refs.cartRowTemplate.content.firstElementChild.cloneNode(true);
        row.dataset.lineKey = key;
        row.dataset.variantId = line.id;
        CustomVolumeBundler.setThumb(row, line.image, line.title);
        const name = row.querySelector('[data-volume-item-name]');
        name.textContent = line.title;
        name.href = line.url;
        row.querySelector('[data-volume-item-price]').textContent = `${this.formatMoney(line.price)} × ${line.qty}`;
        row.querySelector('[data-volume-item-qty]').textContent = line.qty;
        // MARK:- Cap uses the variant-summed cart qty, not line.qty — the same variant can sit on a tagged and an untagged line
        if (line.max != null && (this.inCartMap.get(Number(line.id)) || 0) >= line.max) {
          row.querySelector('[data-volume-cart-inc]').setAttribute('aria-disabled', 'true');
        }
        fragment.appendChild(row);
      });
      this.refs.cartList.replaceChildren(fragment);
      if (this.refs.cartGroup) this.refs.cartGroup.hidden = this.cartState.lines.size === 0;
    }

    // MARK:- In-cart qty edit — optimistic local update + per-line debounced /cart/change;
    // decrementing to 0 removes the line. Sold-out/oversell declines are surfaced by the alert
    // and the panel resyncs from Liquid. Writes are serialized per line (see pushLineChange):
    // /cart/change sets an ABSOLUTE quantity, so two overlapping requests could land out of order.
    changeCartLine(key, delta) {
      const line = this.cartState.lines.get(key);
      if (!line) return;
      // MARK:- Don't POST an increment the cap already rules out — the 422 path below stays as the safety net for concurrent-shopper races
      if (delta > 0 && line.max != null && (this.inCartMap.get(Number(line.id)) || 0) + delta > line.max) {
        this.showAlert(this.maxNote(line.max));
        return;
      }
      const qty = Math.max(0, line.qty + delta);
      const applied = qty - line.qty;
      if (applied === 0) return;

      this.cartState.count += applied;
      this.cartState.subtotal += line.price * applied;
      // MARK:- Applied discount is unknown until /cart/change answers (applyCartData or sectionSync restores it)
      this.cartState.saved = null;
      if (qty === 0) {
        this.cartState.lines.delete(key);
      } else {
        this.cartState.lines.set(key, { ...line, qty });
      }
      this.render();

      this.pendingLineQty.set(key, qty);
      if (this.lineTimers.has(key)) clearTimeout(this.lineTimers.get(key));
      this.lineTimers.set(key, setTimeout(() => this.pushLineChange(key), 300));
    }

    // MARK:- One in-flight /cart/change per line key — while a request is out, newer edits only update
    // pendingLineQty; the latest desired qty is sent once the current request settles
    async pushLineChange(key) {
      this.lineTimers.delete(key);
      if (this.lineInflight.has(key)) return;
      const quantity = this.pendingLineQty.get(key);
      this.pendingLineQty.delete(key);
      if (quantity === undefined) return;
      this.lineInflight.add(key);
      this.showAlert('');

      const cartDrawer = document.querySelector('cart-drawer');
      const sections = ['cart-icon-bubble'];
      if (cartDrawer) sections.push(cartDrawer.closest('.shopify-section').id.replace('shopify-section-', ''));

      // MARK:- Standard storefront event at operation START (action: update) — 422 resolves with
      // userErrors, only a failed request rejects + shopify:cart:error (see docs/javascript-patterns.md)
      const standardEvents = window.StandardEvents;
      let deferred = null;
      if (standardEvents && standardEvents.CartLinesUpdateEvent) {
        deferred = standardEvents.CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(new standardEvents.CartLinesUpdateEvent({
          action: quantity === 0 ? 'remove' : 'update',
          context: 'cart',
          lines: [{ id: key, quantity }],
          promise: deferred.promise
        }));
      }

      try {
        const response = await fetch(theme.routes.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: key, quantity, sections, sections_url: window.location.pathname })
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const error = typeof data.description === 'string' ? data.description : data.message || 'Something went wrong. Please try again.';
          this.showAlert(error);
          this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error } }));
          if (deferred) deferred.resolve({ cart: null, userErrors: [{ code: 'INVALID', field: [], message: error }] });
          this.sectionSync();
          return;
        }

        CustomVolumeBundler.renderCartSections(data, false);

        if (deferred) deferred.resolve({ cart: standardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(data) });
        this.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: { cart: data, lineKey: key, newQuantity: quantity }
        }));

        // MARK:- Skip applying a response that a newer pending edit on the same line will supersede — avoids a stale-qty flash
        if (!this.lineTimers.has(key) && !this.pendingLineQty.has(key)) {
          this.applyCartData(data);
          this.render();
        }
      } catch (err) {
        console.error('custom-volume-bundler: line change failed', err); // eslint-disable-line no-console
        this.showAlert('Something went wrong. Please try again.');
        if (deferred) deferred.reject(err);
        if (standardEvents && standardEvents.CartErrorEvent) {
          this.dispatchEvent(new standardEvents.CartErrorEvent({ error: 'Cart request failed', code: 'SERVICE_UNAVAILABLE' }));
        }
        this.sectionSync();
      } finally {
        this.lineInflight.delete(key);
        // MARK:- Drain the edit that arrived mid-flight (only the latest qty is kept)
        if (this.pendingLineQty.has(key)) this.pushLineChange(key);
      }
    }

    renderTotals(current, next, combined, saveRow) {
      const subtotal = this.cartState.subtotal + this.stagedSubtotal();
      if (this.refs.subtotal) this.refs.subtotal.textContent = this.formatMoney(subtotal);
      const savings = window.NHVolumeSavings;
      const lines = this.savingsLines();

      // MARK:- Callout = the cart's applied discount (cartState.saved) when nothing is staged, else the
      // helper's per-unit rule at the current tier, which matches what the cart applies to the cent
      if (this.refs.saveRow) {
        const callout = savings.savingsCallout(this.tiers, combined, saveRow.cents, (cents) => this.formatMoney(cents));
        this.refs.saveRow.hidden = !callout;
        if (callout) {
          if (this.refs.saveAmount) this.refs.saveAmount.textContent = callout.amount;
          if (this.refs.savePct) this.refs.savePct.textContent = callout.pct;
        }
      }

      // MARK:- Hint = the discount on the full next tier; "about" because the plants still to add are priced at the fill price
      if (this.refs.hint) {
        const estimate = next && combined > 0
          ? savings.estimateNextTier(this.tiers, combined, lines.cart.concat(lines.staged), this.fillPrice())
          : null;
        if (estimate) {
          this.refs.hint.hidden = false;
          if (this.refs.hintText) {
            this.refs.hintText.textContent = `Save about ${this.formatMoney(estimate)} at ${next.qty} ${this.nounPlural}`;
          }
        } else {
          this.refs.hint.hidden = true;
        }
      }
    }

    // MARK:- Card state: stepper/ribbon from staged qty; "N in cart" chip from cartState.lines summed per
    // variant (Liquid seeds it, JS keeps it current after own edits, resyncs, and load-more appends)
    syncCards() {
      this.querySelectorAll('[data-volume-card]').forEach((card) => {
        const variantId = Number(card.dataset.variantId);
        const meta = this.staged.get(variantId);
        const qty = meta ? meta.qty : 0;
        const cartQty = this.inCartMap.get(variantId) || 0;
        const chip = card.querySelector('[data-volume-incart]');
        if (chip) {
          chip.hidden = cartQty === 0;
          const chipQty = chip.querySelector('[data-volume-incart-qty]');
          if (chipQty) chipQty.textContent = cartQty;
        }
        const addBtn = card.querySelector('[data-volume-add]');
        const stepper = card.querySelector('[data-volume-stepper]');
        const qtyEl = card.querySelector('[data-volume-qty]');
        const ribbon = card.querySelector('[data-volume-ribbon]');

        // MARK:- Can't ship to the shopper's state: grey the card, disable its add, and say why where the add button was
        const restricted = window.NHVolumeSavings.isRestricted(
          window.NHVolumeSavings.parseStates(card.dataset.restrictedStates),
          this.shipState
        );
        card.classList.toggle('is-restricted', restricted);
        const restrictedEl = card.querySelector('[data-volume-restricted]');
        if (restrictedEl) {
          restrictedEl.hidden = !restricted;
          restrictedEl.textContent = restricted ? window.NHVolumeSavings.restrictedCopy(this.shipState) : '';
        }
        if (addBtn) {
          addBtn.hidden = qty > 0 || restricted;
          addBtn.disabled = restricted;
        }
        if (stepper) stepper.hidden = qty === 0 || restricted;
        if (qtyEl) qtyEl.textContent = qty;
        if (ribbon) ribbon.hidden = qty === 0;
        card.classList.toggle('is-in-bundle', qty > 0);

        // MARK:- Inventory-cap feedback — dim the control that can't add more, so the silent clamp doesn't read as a broken button
        const absMax = CustomVolumeBundler.cardMax(card);
        const eff = this.effectiveMax(variantId, absMax);
        const maxed = eff !== null && qty >= eff;
        const incBtn = card.querySelector('[data-volume-inc]');
        if (incBtn) {
          if (maxed) incBtn.setAttribute('aria-disabled', 'true');
          else incBtn.removeAttribute('aria-disabled');
        }
        if (addBtn) {
          if (eff === 0) addBtn.setAttribute('aria-disabled', 'true');
          else addBtn.removeAttribute('aria-disabled');
        }

        // MARK:- Availability parenthetical on the stock line — the stock count stays static (staging
        // isn't a purchase); "(Only N available)" appears once the cart holds some (mirrors the Liquid SSR)
        // or at the cap, so a dimmed + is never unexplained even when stock is above the low threshold
        const noteEl = card.querySelector('[data-volume-max-note]');
        const showNote = absMax !== null && (cartQty > 0 || maxed);
        if (noteEl) {
          noteEl.hidden = !showNote;
          noteEl.textContent = showNote ? `(${this.maxNote(eff)})` : '';
        }
        // MARK:- hidden only tracks whether there's anything to say — CSS gates the reveal on the card's .is-in-bundle state
        const stockLine = card.querySelector('[data-volume-stock]');
        if (stockLine) {
          const stockText = stockLine.querySelector('[data-volume-stock-text]');
          const hasStockText = stockText !== null && !stockText.hidden;
          stockLine.hidden = !hasStockText && !showNote;
        }
      });
    }

    pulsePeek() {
      if (!this.refs.peek) return;
      this.refs.peek.classList.remove('is-pulsing');
      // Restart the animation even on rapid successive adds
      void this.refs.peek.offsetWidth; // eslint-disable-line no-void
      this.refs.peek.classList.add('is-pulsing');
    }

    toggleExpanded() {
      if (this.classList.contains('is-expanded')) {
        this.collapse();
      } else {
        this.classList.add('is-expanded');
        if (this.refs.scrim) this.refs.scrim.hidden = false;
        if (this.refs.peek) this.refs.peek.setAttribute('aria-expanded', 'true');
      }
    }

    collapse() {
      this.classList.remove('is-expanded');
      if (this.refs.scrim) this.refs.scrim.hidden = true;
      if (this.refs.peek) this.refs.peek.setAttribute('aria-expanded', 'false');
    }

    showAlert(message) {
      if (!this.refs.alert) return;
      this.refs.alert.hidden = !message;
      this.refs.alert.textContent = message || '';
    }

    // MARK:- Commit — one atomic multi-item add (422 adds nothing, staging survives), then the
    // cart drawer renders from the returned sections; mirrors product-form.js + custom-cart-product-recommendations
    async commit() {
      if (this.staged.size === 0 || this.committing) return;
      this.committing = true;
      this.showAlert('');
      this.hideRecovery();
      if (this.refs.commit) {
        this.refs.commit.classList.add('is-loading');
        this.refs.commit.setAttribute('aria-disabled', 'true');
      }

      // MARK:- Hidden analytics origin on every committed line — underscore-prefixed so cart/checkout/order pages skip it, but it persists to order line item properties (Admin customAttributes / CSV / Flow). The same variant added from a PDP lands on a separate untagged line by design; applyCartData sums per variant_id so tier counts stay correct.
      const items = [];
      this.staged.forEach((meta, id) => {
        items.push({ id, quantity: meta.qty, properties: { ...CustomVolumeBundler.ORIGIN_PROPERTY } });
      });

      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // MARK:- Standard storefront event — dispatched at operation START per the spec, carrying a
      // promise that settles with the outcome; the legacy on:cart:add below still fires on success
      // until listeners migrate. Guarded: the library loads via custom-storefront-events.liquid.
      const standardEvents = window.StandardEvents;
      let deferred = null;
      if (standardEvents && standardEvents.CartLinesUpdateEvent) {
        deferred = standardEvents.CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(new standardEvents.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          // MARK:- CartLinesUpdateLine is merchandiseId/id + quantity only; the _origin property rides in `detail`
          lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
          promise: deferred.promise,
          detail: { properties: { ...CustomVolumeBundler.ORIGIN_PROPERTY } }
        }));
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const error = typeof data.description === 'string' ? data.description : data.message || 'Something went wrong. Please try again.';
          this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error } }));
          // MARK:- The cart DECLINED the change (e.g. 422 sold out) — per the standard-events spec
          // that's a resolve with userErrors, NOT a rejection or a shopify:cart:error
          if (deferred) deferred.resolve({ cart: null, userErrors: [{ code: 'INVALID', field: [], message: error }] });
          // MARK:- Plant Sentry's cart-wide 422 names the plant; offer its removal when we can find it, else the raw message
          const blockedBy = response.status === 422 ? window.NHVolumeSavings.parseShipError(error) : null;
          // MARK:- Plant Sentry judges the delivery address, the cards judge growing_state; when they disagree, adopt
          // the blocked state for this session's render (no localStorage / cart attribute write) so the rest grey out
          if (blockedBy && blockedBy.state !== this.shipState) {
            this.sentryState = blockedBy.state;
            this.shipState = blockedBy.state;
          }
          // MARK:- #2920 share it so the drawer's <ship-filter> and line badges judge the same state
          if (blockedBy) window.NHVolumeSavings.setSentryState(blockedBy.state);
          if (!blockedBy || !(await this.offerRecovery(blockedBy))) this.showAlert(error);
          return;
        }

        // MARK:- Snapshot committed ids into the classification set BEFORE clearing staged —
        // otherwise items staged from a page whose cards aren't currently rendered would be
        // uncounted by applyCartData until the next full page load
        items.forEach((item) => this.cartState.ids.add(item.id));

        this.clearStaged();
        this.collapse();

        if (theme.settings.afterAtc === 'page') {
          // MARK:- cart: null is spec-legal (CartLinesUpdateResult.cart is nullable) — settle now rather than delay the redirect on a /cart.js fetch
          if (deferred) deferred.resolve({ cart: null });
          window.location.href = theme.routes.cart;
          return;
        }

        CustomVolumeBundler.renderCartSections(data, true);

        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          if (deferred) deferred.resolve({ cart: standardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(cartData) });
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { cart: cartData, variantId: items[0].id }
          }));
          this.applyCartData(cartData);
        } else if (deferred) {
          // MARK:- Add succeeded but the cart snapshot fetch didn't — resolve with nullable cart; rejecting would falsely signal the add failed
          deferred.resolve({ cart: null });
        }
        this.render();
      } catch (err) {
        console.error('custom-volume-bundler: commit failed', err); // eslint-disable-line no-console
        this.showAlert('Something went wrong. Please try again.');
        // MARK:- Request FAILED (network/service) — reject the promise and dispatch shopify:cart:error
        if (deferred) deferred.reject(err);
        if (standardEvents && standardEvents.CartErrorEvent) {
          this.dispatchEvent(new standardEvents.CartErrorEvent({ error: 'Cart request failed', code: 'SERVICE_UNAVAILABLE' }));
        }
      } finally {
        this.committing = false;
        if (this.refs.commit) {
          this.refs.commit.classList.remove('is-loading');
          this.refs.commit.removeAttribute('aria-disabled');
        }
        this.render();
      }
    }

    // MARK:- Plant Sentry recovery. The 422 carries only the product title and state, so match it to a
    // staged plant first (unstage it), else to cart lines by product_title (remove them, then re-commit).
    // Matching rules live in NHVolumeSavings.recoveryStagedIds / recoveryCartKeys (fixture-tested).
    async offerRecovery(blockedBy) {
      const savings = window.NHVolumeSavings;
      const ids = savings.recoveryStagedIds([...this.staged.entries()], blockedBy);
      if (ids.length) {
        this.showRecovery({ kind: 'staged', title: blockedBy.title, state: blockedBy.state, ids });
        return true;
      }
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return false;
        const cart = await response.json();
        const keys = savings.recoveryCartKeys(cart.items, this.knownStatesByVariant(), blockedBy);
        if (!keys.length) return false;
        this.showRecovery({ kind: 'cart', title: blockedBy.title, state: blockedBy.state, keys });
        return true;
      } catch (err) {
        console.error('custom-volume-bundler: recovery cart fetch failed', err); // eslint-disable-line no-console
        return false;
      }
    }

    // MARK:- Known restricted lists by variant: rendered cards (a card without the attribute is a known empty list), then staged meta
    knownStatesByVariant() {
      const known = new Map();
      this.staged.forEach((meta, id) => {
        if (Array.isArray(meta.restricted)) known.set(Number(id), meta.restricted);
      });
      this.querySelectorAll('[data-volume-card]').forEach((card) => {
        known.set(Number(card.dataset.variantId), window.NHVolumeSavings.parseStates(card.dataset.restrictedStates));
      });
      return known;
    }

    showRecovery(recovery) {
      this.recovery = recovery;
      if (!this.refs.recovery) return;
      const copy = window.NHVolumeSavings.recoveryCopy(recovery.kind, recovery.title, recovery.state);
      this.refs.recoveryText.textContent = copy.text;
      this.refs.recoveryBtn.textContent = copy.button;
      this.refs.recovery.hidden = false;
    }

    hideRecovery() {
      this.recovery = null;
      if (this.refs && this.refs.recovery) this.refs.recovery.hidden = true;
    }

    async runRecovery() {
      const recovery = this.recovery;
      if (!recovery || this.recovering || !this.refs.recoveryBtn) return;
      if (recovery.kind === 'staged') {
        recovery.ids.forEach((id) => this.staged.delete(id));
        this.persist();
        this.hideRecovery();
        this.render();
        return;
      }

      // MARK:- Cart line removal: /cart/change.js qty 0 per matched line, drawer + bubble from the returned
      // sections, then on:line-item:change for the theme's other cart listeners; then one retry of the commit
      const btn = this.refs.recoveryBtn;
      const cartDrawer = document.querySelector('cart-drawer');
      const drawerSection = cartDrawer && cartDrawer.closest('.shopify-section');
      const sections = ['cart-icon-bubble'];
      if (drawerSection) sections.push(drawerSection.id.replace('shopify-section-', ''));
      // MARK:- Flag set after the drawer lookup so a lookup throw can't leave the button stuck loading
      this.recovering = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-disabled', 'true');
      let removed = false;
      try {
        let data = null;
        for (const key of recovery.keys) {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(`${theme.routes.cartChange}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: key, quantity: 0, sections, sections_url: window.location.pathname })
          });
          // eslint-disable-next-line no-await-in-loop
          data = await response.json().catch(() => null);
          if (!response.ok || !data) throw new Error(`cart change ${response.status}`);
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: { cart: data, lineKey: key, newQuantity: 0 }
          }));
        }
        CustomVolumeBundler.renderCartSections(data, false);
        this.applyCartData(data);
        this.hideRecovery();
        removed = true;
      } catch (err) {
        console.error('custom-volume-bundler: recovery removal failed', err); // eslint-disable-line no-console
        this.showAlert('Something went wrong. Please try again.');
        this.sectionSync();
      } finally {
        this.recovering = false;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-disabled');
      }
      if (removed) await this.commit();
    }

    // MARK:- Cart drawer (re-renders the header bubble too) or, without a drawer, just the bubble
    static renderCartSections(data, open) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.renderContents(data, open);
      } else if (data && data.sections && data.sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
      }
    }

    scheduleCartSync() {
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.sectionSync(), 150);
    }

    // MARK:- Liquid-accurate resync via the Section Rendering API — re-reads the data-cart-*
    // attributes and the per-card "N in cart" chips from a fresh render of this section, so
    // matched items added anywhere (PDP, other tabs, apps) are counted even when their product
    // isn't in the rendered grid. Falls back to the cart.js heuristic if the fetch fails.
    async sectionSync() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) {
        this.cartSync();
        return;
      }
      try {
        // MARK:- Preserve the current query (page, sort_by, filters) so the refetch matches the rendered state
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`section fetch ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('custom-volume-bundler');
        if (!fresh) throw new Error('section markup missing bundler root');

        // MARK:- data-cart-lines covers every matched line regardless of which cards are rendered; chips derive from it in syncCards
        this.cartState = CustomVolumeBundler.readCartState(fresh);
        this.dataset.shipState = fresh.dataset.shipState || '';
        this.shipState = this.sentryState || this.resolveShipState();

        // MARK:- Carry the fresh Liquid inventory onto the live cards (data-max + stock hint) so reclampStaged and the cap UI never work from a stale render
        const freshCards = new Map();
        doc.querySelectorAll('[data-volume-card]').forEach((card) => freshCards.set(card.dataset.variantId, card));
        this.querySelectorAll('[data-volume-card]').forEach((card) => {
          const freshCard = freshCards.get(card.dataset.variantId);
          if (!freshCard) return;
          if (freshCard.dataset.max !== undefined) card.dataset.max = freshCard.dataset.max;
          else delete card.dataset.max;
          const liveStock = card.querySelector('[data-volume-stock]');
          const freshStock = freshCard.querySelector('[data-volume-stock]');
          if (liveStock && freshStock) liveStock.replaceWith(freshStock);
        });

        this.render();
      } catch (err) {
        console.error('custom-volume-bundler: section sync failed, falling back to cart.js', err); // eslint-disable-line no-console
        this.cartSync();
      }
    }

    async cartSync() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return;
        this.applyCartData(await response.json());
        this.render();
      } catch (err) {
        console.error('custom-volume-bundler: cart sync failed', err); // eslint-disable-line no-console
      }
    }

    // MARK:- Classification gap — cart.js exposes no variant metafields, so matched lines are
    // identified by id membership: Liquid-seeded cart ids ∪ grid card ids ∪ staged ids. A matched
    // item added in another tab from a product outside this set is uncounted until next page load.
    applyCartData(cartData) {
      const knownIds = new Set(this.cartState.ids);
      this.staged.forEach((meta, id) => knownIds.add(id));
      this.querySelectorAll('[data-volume-card]').forEach((card) => {
        const id = Number(card.dataset.variantId);
        if (id) knownIds.add(id);
      });

      const state = { count: 0, subtotal: 0, saved: 0, ids: new Set(this.cartState.ids), lines: new Map() };
      cartData.items.forEach((item) => {
        if (!knownIds.has(item.variant_id)) return;
        state.count += item.quantity;
        state.subtotal += item.original_line_price;
        state.saved += item.line_level_total_discount || 0;
        state.ids.add(item.variant_id);
        // MARK:- cart.js carries no inventory — keep the cap from the line we already knew, else from the
        // variant's rendered card (covers fresh post-commit lines); otherwise uncapped until the next section sync
        const prev = this.cartState.lines.get(item.key);
        let max = prev && prev.max != null ? prev.max : null;
        if (max == null) {
          const card = this.querySelector(`[data-volume-card][data-variant-id="${item.variant_id}"]`);
          if (card) max = CustomVolumeBundler.cardMax(card);
        }
        state.lines.set(item.key, {
          key: item.key,
          id: item.variant_id,
          qty: item.quantity,
          price: item.original_price,
          max,
          title: item.product_title,
          url: item.url,
          image: CustomVolumeBundler.thumbUrl(item.image)
        });
      });
      this.cartState = state;
    }

    // MARK:- Row templates ship a span placeholder (no empty <img src="">); swap in a real image only when there is a URL
    static setThumb(row, src, alt) {
      const placeholder = row.querySelector('[data-volume-item-thumb]');
      if (!placeholder || !src) return;
      const img = document.createElement('img');
      img.className = placeholder.className;
      img.src = src;
      img.alt = alt || '';
      img.width = 44;
      img.height = 44;
      img.loading = 'lazy';
      placeholder.replaceWith(img);
    }

    // MARK:- cart.js returns full-size image URLs; request the 120px CDN rendition the Liquid rows use
    static thumbUrl(url) {
      if (!url) return '';
      return `${url}${url.includes('?') ? '&' : '?'}width=120`;
    }
  }

  CustomVolumeBundler.CART_EVENTS = ['on:cart:add', 'on:line-item:change', 'on:cart-items:refresh'];
  // MARK:- grow-zone-finder.js events that can carry a new shopper state
  CustomVolumeBundler.ZONE_EVENTS = ['zone:updated', 'cart:attribute-updated'];
  // MARK:- Private line property stamped on bundler adds; read it from order line items for attribution
  CustomVolumeBundler.ORIGIN_PROPERTY = { _origin: 'volume-bundler' };

  customElements.define('custom-volume-bundler', CustomVolumeBundler);
}
